'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useTrack } from '@owlcat/app-shell';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { cn } from '@/shared/utils/cn';
import { ChevronLeft, ChevronRight, Loader2, Sparkles, X } from 'lucide-react';
import { WizardStepper } from './WizardStepper';
import type { WizardMode } from './ModeStep';
import { HeroesStep } from './HeroesStep';
import type { InputMode } from './InputModeStep';
import { VoiceChoiceStep } from './VoiceChoiceStep';
import {
  EMPTY_LIBRARY,
  jobFromParam,
  roadFor,
  type JobId,
  type LibrarySnapshot,
} from '../../services/jobRoads';
import { DictateStep, type DictateHeroesPicked } from './DictateStep';
import { RecordStep } from './RecordStep';
import { StorySelectStep } from './StorySelectStep';
import { StoryInputStep } from './StoryInputStep';
import type { StoryBrief } from '@/contexts/media/realtimeVoice';
import { OutputStep, type BookStyle, type AudioOptions, type IllustrationOptions } from './OutputStep';
import { TranslateStep } from './TranslateStep';
import { getIllustrationsByStory, type IllustrationFormat } from '@/contexts/illustrations';
import { getAudioStoriesByStory } from '@/contexts/audio-stories';
import { type ContentParameters } from './ParametersStep';
import { ReviewStep } from './ReviewStep';
import { DEFAULT_VOICE_SETTINGS, type VoiceSettings } from './VoiceStep';
import { useStartContentCreation } from '../../hooks/useStartContentCreation';
import type { WizardScenario } from '../../types/enums';
import { resolveScenario, resolveInitialStoryLanguage } from '../../services/wizardScenario';
import { buildCreateParams, DEFAULT_EXPORT_OPTIONS, type ExportOptions } from '../../services/wizardPayload';
import { heroesAskOf } from '@/contexts/pipeline-v2/order/fromWizard';
import { AskHeroesDialog } from './AskHeroesDialog';
import { photoHeroNameMissing } from './PhotoHeroes';
import { HeroNamesDialog } from './HeroNamesDialog';
import type { HeroNameAsk } from '@/contexts/characters/actions/heroNamesForLanguage';
import { mapBriefToParameters } from '../../services/mapBriefToParameters';
import { saveVoiceCharacters } from '@/contexts/characters';
import { locales, type Locale } from '@/shared/i18n';
import { openSubscription } from '@/contexts/subscriptions/lib/accountsTopUp';
import { openExternalUrl } from '@/shared/utils/openExternal';
import { useTelegramMiniApp } from '@/contexts/authentication/client';
import { bookSizesFor, DEFAULT_BOOK_SIZE, clampPageCount, estimateStoryBlockCount } from '@/contexts/stories/utils/storyShape';
import { DEFAULT_BOOK_FORMAT } from '@/contexts/illustrations/services/bookFormat';
import { extendBookDefaults } from './extendDefaults';

// ── Recent characters ──
const STORAGE_KEY_RECENT_CHARS = 'owlcat-recent-characters';
const MAX_RECENT_CHARACTERS = 5;

const loadRecentCharacters = (): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(STORAGE_KEY_RECENT_CHARS);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveRecentCharacters = (ids: string[]) => {
  const current = loadRecentCharacters();
  const merged = [...new Set([...ids, ...current])].slice(0, MAX_RECENT_CHARACTERS);
  localStorage.setItem(STORAGE_KEY_RECENT_CHARS, JSON.stringify(merged));
};

// ── Wizard state persistence ──
const WIZARD_STORAGE_KEY = 'owlcat-wizard-state';

interface WizardPersistedState {
  wizardMode: WizardMode | null;
  inputMode: InputMode | null;
  parameters: ContentParameters;
  wantAudio: boolean;
  wantIllustrations: boolean;
  illustrationStyle: BookStyle;
  illustrationFormat?: string;
  voiceSettings: VoiceSettings;
  audioOptions: AudioOptions;
  illustrationOptions: IllustrationOptions;
  exportOptions?: ExportOptions;
  sourceStoryId: string | null;
  sourceStoryTitle: string | null;
  currentStep: number;
  planId: string | null;
}

function saveWizardState(state: WizardPersistedState, key: string = WIZARD_STORAGE_KEY) {
  if (typeof window === 'undefined') return;
  try { sessionStorage.setItem(key, JSON.stringify(state)); } catch {}
}

function loadWizardState(key: string = WIZARD_STORAGE_KEY): WizardPersistedState | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
}

function clearWizardState(key: string = WIZARD_STORAGE_KEY) {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(key);
}

// ── Step sequences ──
// A JOB walks its own road (services/jobRoads.ts): the screens it needs, in
// order, with everything else decided. These sequences are the entries that are
// NOT a job — an existing story being extended, a remix, a translation.
const STEPS_EXTEND = ['storySelect', 'output', 'review'] as const;
// Remix seeds a fresh generate story with heroes already carried over from the
// source, so it starts at the plot.
const STEPS_REMIX = ['storyInput', 'output', 'review'] as const;
// Story preselected (modal from the workspace): only what to add.
const STEPS_EXTEND_PRESELECTED = ['output', 'review'] as const;
// Translation: which language → review.
const STEPS_TRANSLATE = ['translate', 'review'] as const;

/**
 * Which screens this entry shows.
 *
 * A job's road comes from the job; everything else keeps its own short
 * sequence. `ModeStep` ("what are we doing") and `InputModeStep` ("how do we
 * type it in") are gone as screens: every entry now arrives with both answers
 * already in it.
 */
function resolveSteps(a: {
  job?: JobId;
  library?: LibrarySnapshot;
  wizardMode: WizardMode | null;
  preselectedStoryId?: string | null;
  remixParams?: unknown;
}): readonly string[] {
  if (a.wizardMode === 'translate') return STEPS_TRANSLATE;
  if (a.preselectedStoryId) return STEPS_EXTEND_PRESELECTED;
  if (a.remixParams) return STEPS_REMIX;
  if (a.job) return roadFor(a.job, a.library ?? EMPTY_LIBRARY).screens;
  if (a.wizardMode === 'extend') return STEPS_EXTEND;
  // No job and no entry of its own: the tiles are the door, so this is only
  // reachable by hand. Walk the `hero` road — the order the product sells.
  return roadFor('hero', a.library ?? EMPTY_LIBRARY).screens;
}

/** Derive scenario from outputs + creation mode */
// Narrow an arbitrary string to the canonical Locale union (shared/i18n).
function isLocale(v: string | undefined): v is Locale {
  return !!v && (locales as readonly string[]).includes(v);
}

// deriveScenario / resolveScenario moved to ../../services/wizardScenario (pure,
// unit-tested against every reachable input combination).

interface ContentCreationWizardProps {
  locale: string;
  initialStoryId?: string;
  preselectedStoryId?: string;
  /** Block count of preselected story (for accurate cost estimation) */
  preselectedStoryBlockCount?: number;
  /** Total characters of the preselected story's blocks (exact audio forecast) */
  preselectedStoryTotalChars?: number;
  /** Language of preselected story (overrides locale-based default for voice picker) */
  preselectedStoryLanguage?: string;
  /** Age group of the preselected story (default comic sub-style for a new book on it) */
  preselectedStoryAgeGroup?: string;
  asModal?: boolean;
  onComplete?: (result: { type: string; entityId: string; planId?: string }) => void;
  /** Called immediately when the creation mutation starts (before server responds) */
  onMutationStart?: () => void;
  defaultScenario?: string;
  /** Remix: seed a fresh GENERATE story from an existing story's recoverable
   *  params (language, genre, ageGroup, characters). Empty prompt = new plot,
   *  same heroes. Skips the mode step. */
  remixParams?: Partial<ContentParameters>;
  /** Deep-link from a gallery card: open straight in from-text/based_on with this
   *  published+reusable story preselected as the basis ("same story, own heroes"). */
  initialBasis?: { storyId: string; title?: string };
  /** The JOB the parent came with (from a tile or a landing CTA). It names the
   *  road: which screens are shown and what is decided in advance
   *  (services/jobRoads.ts). Older ids (`illustrated`, `personal`, `series`)
   *  map onto the job they really were. */
  initialIntent?: WizardIntent;
  /** What the family already has — the road asks less on the second visit. */
  library?: LibrarySnapshot;
}

export type WizardIntent =
  | 'hero'       // a book about the child: photo panel + pages drawn for them
  | 'bedtime'    // tonight: text and narration, no book
  | 'together'   // the children compose it out loud with the interviewer
  | 'voice'      // narration in the parent's own voice
  | 'illustrated' | 'personal' | 'series' | 'basedon'; // older tile ids, still honoured

export function ContentCreationWizard({
  locale,
  initialStoryId,
  preselectedStoryId,
  preselectedStoryBlockCount,
  preselectedStoryTotalChars,
  preselectedStoryLanguage,
  preselectedStoryAgeGroup,
  asModal,
  onComplete,
  onMutationStart,
  defaultScenario,
  remixParams,
  initialBasis,
  initialIntent,
  library,
}: ContentCreationWizardProps) {
  const t = useTranslations('content.wizard');

  // ── The road this entry walks ──
  // A basis deep-link from the gallery is the `basedon` road with the story
  // already chosen; everything else names its job in the URL.
  const job: JobId | undefined = jobFromParam(initialIntent) ?? (initialBasis ? 'basedon' : undefined);
  const lib = library ?? EMPTY_LIBRARY;
  const road = job ? roadFor(job, lib) : null;
  const intentBasedOn = job === 'basedon';
  // Entry decided the input method, so the picker is not a screen any more.
  const inputLocked = !!(job || initialBasis);

  const tRate = useTranslations('subscription.rateLimit');
  const tShort = useTranslations('subscription.allowanceShort');
  const { webApp } = useTelegramMiniApp();
  const router = useRouter();

  // ── Restore persisted state ──
  // Skip saved state when explicit entry-point props are provided to prevent
  // stale mode (e.g. translate) from leaking into a fresh creation session.
  const hasFreshEntryPoint = !!(defaultScenario || preselectedStoryId || remixParams || initialBasis || initialIntent);
  /**
   * Where this session's values survive a reload. A job tile (?job=…) is a fresh entry, but
   * its own: it keeps its values under a key of that job, so nothing leaks into another
   * entry and a reload — the parent's F5, or the router falling back to a full page load on
   * a slow connection — brings back what they typed. The other fresh entries (a preselected
   * story, translate, remix, a basis) still keep nothing.
   */
  const jobOnlyEntry = !!initialIntent && !(defaultScenario || preselectedStoryId || remixParams || initialBasis);
  const storageKey: string | null = jobOnlyEntry
    ? `${WIZARD_STORAGE_KEY}:job:${initialIntent}`
    : hasFreshEntryPoint ? null : WIZARD_STORAGE_KEY;

  // ── Wizard mode ──
  // Nothing asks any more: a job (or a remix / basis) is a new story, a
  // preselected story is an extension, SC-07 is a translation.
  const [wizardMode, setWizardMode] = useState<WizardMode | null>(
    remixParams || initialBasis || job
      ? 'new'
      : defaultScenario === 'SC-07' ? 'translate' : (preselectedStoryId || initialStoryId ? 'extend' : 'new'),
  );

  // ── Input mode ──
  // Comes from the road (which tab of the story-input screen opens); kept as
  // state because the dictation screens switch it when they hand off.
  const [inputMode, setInputMode] = useState<InputMode | null>(
    road ? (road.input as InputMode) : (initialBasis ? 'fromText' : 'typePick'),
  );

  /**
   * Telling a story OUT LOUD asks for one back: a child who has just spent
   * five minutes inventing it with their voice should not be handed a text
   * file. Switching into dictation turns narration on — once, and never over
   * a parent who has already decided for themselves.
   */
  useEffect(() => {
    if (inputMode === 'dictate' && !audioTouched.current) setWantAudio(true);
  }, [inputMode]);

  // ── Steps tracking ──
  // Step 1 is always the road's first screen.
  const [currentStep, setCurrentStep] = useState(1);

  // ── Story selection (extend mode) ──
  const [sourceStoryId, setSourceStoryId] = useState<string | null>(preselectedStoryId ?? initialStoryId ?? null);
  const [sourceStoryTitle, setSourceStoryTitle] = useState<string | null>(null);
  const [sourceStoryBlockCount, setSourceStoryBlockCount] = useState<number | undefined>(preselectedStoryBlockCount);
  const [sourceStoryTotalChars, setSourceStoryTotalChars] = useState<number | undefined>(preselectedStoryTotalChars);

  // ── Parameters ──
  // For extend/translate flows the source story's language must drive voice
  // resolution, otherwise voiceMappings lookup uses the page locale and picks
  // a voice in the wrong language (cs story + ru locale → ru/male picked).
  // Default to the language the user is actually looking at (interface locale), not the
  // page-prop locale which can be their stored preferredLocale.
  const uiLocale = useLocale();
  const track = useTrack();
  const initialLang: Locale = resolveInitialStoryLanguage(preselectedStoryLanguage, uiLocale, locale);
  const [parameters, setParameters] = useState<ContentParameters>({
    prompt: '',
    language: initialLang,
    characterIds: [],
    characterAssignments: [],
    // The road's own answers to genre / age / length / energy — the values the
    // first-story screen used to hard-code, now owned by the job.
    genre: (road?.params.genre ?? 'adventure'),
    ageGroup: (road?.params.ageGroup ?? '5-7'),
    energy: road?.params.energy ?? 3,
    // Length in pages. A saved road still speaks in minutes, so its answer is
    // converted once here instead of being carried as a second source of truth.
    pageCount: road?.params.desiredDurationMinutes
      ? clampPageCount(
          estimateStoryBlockCount(road.params.desiredDurationMinutes, road?.params.ageGroup),
          road?.params.ageGroup,
        )
      : bookSizesFor(road?.params.ageGroup)[DEFAULT_BOOK_SIZE],
    desiredDurationMinutes: road?.params.desiredDurationMinutes ?? 7,
    creationMode: road?.creationMode ?? 'generate',
    ...(road?.fromTextMode ? { fromTextMode: road.fromTextMode } : {}),
    // Remix: seed recoverable params (genre/ageGroup/language/characters);
    // prompt stays empty so the parent writes a NEW plot for the same heroes.
    ...(remixParams ?? {}),
    // Basis deep-link: from-text/based_on with the gallery story preselected.
    ...(initialBasis
      ? {
          creationMode: 'from-text' as const,
          fromTextMode: 'based_on' as const,
          basisStoryId: initialBasis.storyId,
          ...(initialBasis.title ? { title: initialBasis.title } : {}),
        }
      : {}),
    // Outcome-tile "based_on" intent: from-text/based_on, no preselected story
    // (the basis picker + change vector are shown for the parent to choose).
    ...(intentBasedOn
      ? { creationMode: 'from-text' as const, fromTextMode: 'based_on' as const }
      : {}),
  });

  // ── Recent characters ──
  const [recentCharacterIds, setRecentCharacterIds] = useState<string[]>([]);
  useEffect(() => {
    setRecentCharacterIds(loadRecentCharacters());
  }, []);

  // ── Output options ──
  // The road decides what the order delivers. `hero` had this wrong: it drew
  // its pages (`autoGenerateImages`) but never ordered the book they belong to,
  // so the landing's promise came back as text and narration.
  /**
   * Telling a story OUT LOUD asks for one back.
   *
   * A child who has just spent five minutes inventing it with their voice is
   * handed a text file unless somebody ticks narration — and the one thing
   * they can do with the result, alone, in bed, is listen to it. Dictation
   * defaults to an audio story; every other road keeps its own default, and
   * the tick is right there to turn off.
   */
  const [wantAudio, setWantAudio] = useState(
    road?.wantAudio
      ?? (defaultScenario === 'SC-02'
        || defaultScenario === 'SC-05'
        || (road ? (road.input as InputMode) === 'dictate' : false)),
  );
  /** The parent moved the narration tick themselves — stop guessing for them. */
  const audioTouched = useRef(false);
  const chooseAudio = useCallback((on: boolean) => {
    audioTouched.current = true;
    setWantAudio(on);
  }, []);
  const [wantIllustrations, setWantBook] = useState(
    road?.wantIllustrations ?? (defaultScenario === 'SC-03' || defaultScenario === 'SC-06'),
  );
  const [illustrationStyle, setBookStyle] = useState<BookStyle>('watercolor');
  // A vertical 2:3 page (owner decision 2026-10-31): it prints on A5 / A4
  // portrait and every image model orders 2:3 natively (bookFormat.ts).
  const [illustrationFormat, setBookFormat] = useState<IllustrationFormat>(DEFAULT_BOOK_FORMAT);
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings>(DEFAULT_VOICE_SETTINGS);

  // EXTEND inherits what the story already has: the latest book's style
  // and the latest audio layer's voice/quality. Before, every "+ audio/+ book"
  // re-asked with defaults (a second book to an anime story came out watercolor;
  // a new narration got the preset voice instead of the one already used).
  useEffect(() => {
    if (wizardMode !== 'extend' || !sourceStoryId) return;
    let cancelled = false;
    (async () => {
      const [books, audios] = await Promise.all([
        getIllustrationsByStory(sourceStoryId).catch(() => []),
        getAudioStoriesByStory(sourceStoryId).catch(() => []),
      ]);
      if (cancelled) return;
      // The style carries over; the format does not (extendDefaults.ts).
      const inherited = extendBookDefaults(books[0]);
      if (inherited.style) setBookStyle(inherited.style as BookStyle);
      const audio = audios.find((a) => !!a.defaultVoiceId) ?? audios[0];
      if (audio?.defaultVoiceId) {
        setVoiceSettings((v) => ({
          ...v,
          voiceId: audio.defaultVoiceId as string,
          voiceName: audio.defaultVoiceName || (audio.defaultVoiceId as string),
          ...(audio.voiceStability != null ? { stability: audio.voiceStability } : {}),
          ...(audio.voiceSimilarity != null ? { similarityBoost: audio.voiceSimilarity } : {}),
        }));
      }
    })();
    return () => { cancelled = true; };
  }, [wizardMode, sourceStoryId]);
  const [audioOptions, setAudioOptions] = useState<AudioOptions>({ autoGenerateBlocks: true, autoAssemble: true });
  // `bedtime` is the one order with no book at all: a tale to put on tonight.
  const [illustrationOptions, setBookOptions] = useState<IllustrationOptions>({
    autoGeneratePortraits: true,
    // Whether the pages are drawn with the order or left as a second request.
    // A job that promises a book draws them: leaving the pages for later is why
    // the old `personal` tile delivered portraits and a cover and nothing to
    // read (gap 3).
    autoGenerateImages: road?.autoGenerateImages ?? true,
  });
  // Exports of the order (pipeline v2): PDF with every book; print and video asked for.
  const [exportOptions, setExportOptions] = useState<ExportOptions>(DEFAULT_EXPORT_OPTIONS);
  // «Спросить родителя»: heroes picked, the operation does not use them.
  const [askHeroes, setAskHeroes] = useState<'keep' | 'retell' | null>(null);
  const [characterNames, setCharacterNames] = useState<Record<string, string>>({});
  /** The review screen's "change" panel (the retired output step, on demand). */
  const [showOutputEdit, setShowOutputEdit] = useState(false);

  // ── Translate mode ──
  const [targetLanguage, setTargetLanguage] = useState<string>('');
  /** "Translate the text only" — do not reproduce the source's book/narration. */
  const [translateTextOnly, setTranslateTextOnly] = useState(false);
  /**
   * What the source story HAS. A translation reproduces those layers, so both
   * the translate step and the review line are read from this — the review
   * screen said "Only the story" while a book and a narration were on order.
   */
  const [sourceLayers, setSourceLayers] = useState<{ hasBook: boolean; hasNarration: boolean }>(
    { hasBook: false, hasNarration: false },
  );

  useEffect(() => {
    if (wizardMode !== 'translate' || !sourceStoryId) {
      setSourceLayers({ hasBook: false, hasNarration: false });
      return;
    }
    let alive = true;
    void (async () => {
      const { getStoryLayers } = await import('@/contexts/content/actions/getStoryLayers');
      const layers = await getStoryLayers(sourceStoryId).catch(() => null);
      if (alive && layers) setSourceLayers(layers);
    })();
    return () => { alive = false; };
  }, [wizardMode, sourceStoryId]);

  // ── Error state ──
  const [error, setError] = useState<string | null>(null);
  // Free text-gen fair-use throttle → non-blocking soft-push to subscribe.
  const [rateLimited, setRateLimited] = useState(false);
  /** The allowance cannot pay for the whole book: its numbers, not the free-limit copy. */
  const [allowanceShort, setAllowanceShort] = useState<{ needed: number; available: number; pages: number } | null>(null);

  // ── Cost confirmation & creation flow ──
  const [isCreating, setIsCreating] = useState(false);

  // Funnel: an order was opened, and with which job. Paired with the job
  // page's own pageview, this is the share of a page's readers who start.
  useEffect(() => {
    track('create_started', { app_id: 'portal', entry: `wizard:${job ?? 'none'}`, language: initialLang });
    // once per mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Restore from sessionStorage after hydration ──
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (!storageKey) { setHydrated(true); return; }
    const saved = loadWizardState(storageKey);
    if (saved) {
      if (saved.wizardMode != null) setWizardMode(saved.wizardMode);
      if (saved.inputMode != null) setInputMode(saved.inputMode);
      // NOTE: the STEP is NOT restored from sessionStorage — the URL (#step=) is the
      // single source of truth for navigation (browser Back/Forward), so restoring a
      // step here would fight the URL. Form VALUES are restored; the step comes from
      // the URL-init effect (or defaults to the first step).
      if (saved.sourceStoryId) setSourceStoryId(saved.sourceStoryId);
      if (saved.sourceStoryTitle) setSourceStoryTitle(saved.sourceStoryTitle);
      if (saved.parameters) setParameters(saved.parameters);
      if (saved.wantAudio) setWantAudio(true);
      if (saved.wantIllustrations) setWantBook(true);
      if (saved.illustrationStyle) setBookStyle(saved.illustrationStyle);
      if (saved.illustrationFormat) setBookFormat(saved.illustrationFormat as IllustrationFormat);
      if (saved.voiceSettings) setVoiceSettings(saved.voiceSettings);
      if (saved.audioOptions) setAudioOptions(saved.audioOptions);
      if (saved.illustrationOptions) setBookOptions(saved.illustrationOptions);
      if (saved.exportOptions) setExportOptions(saved.exportOptions);
    }
    setHydrated(true);
  }, [storageKey]);

  // ── Persist wizard state (only after hydration to avoid overwriting with defaults) ──
  // Fresh-entry sessions (preselected story / translate modal) NEVER persist:
  // their state belongs to that entry point only. Persisting them leaked
  // extend-mode into the next plain "new story" open whenever the modal
  // didn't unmount (cleanup-based clearing is not guaranteed).
  useEffect(() => {
    if (!hydrated || !storageKey) return;
    saveWizardState({
      wizardMode, inputMode, parameters, wantAudio, wantIllustrations,
      illustrationStyle, illustrationFormat, voiceSettings, audioOptions, illustrationOptions, exportOptions,
      sourceStoryId, sourceStoryTitle, currentStep, planId: null,
    }, storageKey);
  }, [hydrated, storageKey, wizardMode, inputMode, parameters, wantAudio, wantIllustrations, illustrationStyle, illustrationFormat, voiceSettings, audioOptions, illustrationOptions, exportOptions, sourceStoryId, sourceStoryTitle, currentStep]);

  // ── Clear persisted state on unmount / when entry-point props change ──
  useEffect(() => {
    // a fresh entry drops the plain wizard's leftovers; its own job key (if any) is kept for a reload
    if (hasFreshEntryPoint) clearWizardState();
    return () => { clearWizardState(); };
  }, [hasFreshEntryPoint]);

  // ── Derived scenario ──
  const scenario: WizardScenario | null =
    resolveScenario(wizardMode, parameters.creationMode, wantAudio, wantIllustrations);

  const needsVoice = wantAudio;

  // ── Step sequence ──
  const getSteps = () =>
    resolveSteps({
      job,
      library: lib,
      wizardMode,
      // A story named in the URL is as chosen as one passed by the workspace:
      // either way the parent has already picked it, so the selector is skipped.
      preselectedStoryId: preselectedStoryId ?? initialStoryId,
      remixParams,
    });
  const steps = getSteps();
  // Live handle to the CURRENT sequence — callbacks memoised with [] must not
  // read a stale getSteps closure (e.g. the dictate handler computing the output
  // index against STEPS_NEW instead of STEPS_NEW_DICTATE, landing on review).
  const stepsRef = useRef<readonly string[]>(steps);
  stepsRef.current = steps;
  const currentStepKey = steps[currentStep - 1];
  const reviewStepNum = steps.indexOf('review') + 1;
  const totalSteps = steps.length;

  // ── Step ↔ URL (browser Back/Forward) ─────────────────────────────────
  // Reflect the step in the URL HASH (#step=<key>) via history.pushState so the
  // browser Back/Forward buttons walk the wizard. PURE CLIENT (pushState +
  // popstate, reading window.location) — never router.push/replace.
  //
  // The hash, not ?step=: Next syncs pushState into its router, and a changed
  // SEARCH on this dynamic page (it reads searchParams) is a server round trip
  // (an RSC fetch per step). Under load that fetch sometimes ended in the
  // router's fallback to a full page load — the wizard remounted on the review
  // step with the parent's answers gone. A hash changes no server data.
  // A ?step= from an old link is still read, and dropped from the URL.
  const stepUrl = useCallback((key: string) => {
    const u = new URL(window.location.href);
    u.searchParams.delete('step');
    u.hash = `step=${key}`;
    return u.pathname + u.search + u.hash;
  }, []);
  const urlStepKey = () =>
    new URLSearchParams(window.location.hash.replace(/^#/, '')).get('step')
    ?? new URLSearchParams(window.location.search).get('step');
  const didInitUrl = useRef(false);
  useEffect(() => {
    if (asModal || didInitUrl.current) return;
    didInitUrl.current = true;
    const seq = stepsRef.current;
    const urlStep = urlStepKey();
    const idx = urlStep ? seq.indexOf(urlStep) : -1;
    if (idx >= 0) {
      setCurrentStep(idx + 1); // adopt a valid #step= / ?step= (deep link / refresh)
      // a legacy ?step= moves into the hash (no server round trip from here on)
      if (new URLSearchParams(window.location.search).has('step')) window.history.replaceState(null, '', stepUrl(urlStep!));
    } else {
      // Seed the current step into the URL WITHOUT a new history entry.
      window.history.replaceState(null, '', stepUrl(seq[currentStep - 1]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (asModal) return;
    const onPop = () => {
      const seq = stepsRef.current;
      const urlStep = urlStepKey();
      const idx = urlStep ? seq.indexOf(urlStep) : 0; // no step → first step
      setCurrentStep(idx >= 0 ? idx + 1 : 1);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startMutation = useStartContentCreation();

  // ── Dictate → brief mapping ──
  // The voice agent hands off a StoryBrief; map it onto the SAME ContentParameters
  // the type&pick flow produces (prompt = synopsis + readable hero/theme/setting/
  // energy hints), then jump straight to `output`. No generation happens here — the
  // human still reviews + pays on the normal path.
  const handleDictateBrief = useCallback(async (brief: StoryBrief, heroes: DictateHeroesPicked) => {
    const norm = (s: string) => s.trim().toLowerCase();

    // 1) Build the authoritative hero set from BOTH the picker (tool-driven) AND
    //    the names the agent listed in the brief — the agent often just narrates
    //    "Уля и Мурзик" in submit_story_brief.characters without calling the
    //    select/add tools, and those heroes must still reach the story.
    const existingIds = new Set(heroes.selectedCharacterIds);
    const toCreate: { name: string; description?: string }[] = [...heroes.newCharacters];
    const isQueued = (name: string) => toCreate.some((c) => norm(c.name) === norm(name));

    for (const bc of brief.characters ?? []) {
      const name = bc.name?.trim();
      if (!name) continue;
      const match = heroes.existingCharacters.find((e) =>
        [e.name, ...(e.synonyms ?? [])].some((k) => norm(k) === norm(name)),
      );
      if (match) {
        existingIds.add(match.id); // reuse the library character
      } else if (!isQueued(name)) {
        toCreate.push({ name, description: bc.description }); // save as new
      }
    }

    // 2) Persist every NEW hero into the library (+ portrait, async in the worker)
    //    so it's reused by id. Names that fail to save fall back to free-text.
    let savedIds: string[] = [];
    const savedNames = new Set<string>();
    if (toCreate.length > 0) {
      const res = await saveVoiceCharacters(toCreate);
      if (res.success && res.data) {
        savedIds = res.data.created.map((c) => c.id);
        res.data.created.forEach((c) => savedNames.add(norm(c.name)));
      }
    }

    // 3) Any hero we couldn't save stays in the brief's character list so the
    //    prompt mapping still mentions it (saved ones are structured, not text).
    const unsaved = (brief.characters ?? []).filter(
      (c) => c.name?.trim() && !savedNames.has(norm(c.name)) &&
        !heroes.existingCharacters.some((e) =>
          [e.name, ...(e.synonyms ?? [])].some((k) => norm(k) === norm(c.name)),
        ),
    );
    const merged: StoryBrief = { ...brief, characters: unsaved };

    // 4) Fill ContentParameters from the voice brief (pure + unit-tested mapping)
    //    and REUSE every resolved/created library character via characterIds.
    setParameters((p) => ({
      ...p,
      ...mapBriefToParameters(merged, p),
      characterIds: Array.from(new Set([...p.characterIds, ...existingIds, ...savedIds])),
    }));

    // Land on the heroes step (prefilled with the agent's picks — add/remove
    // library heroes), then the prefilled story-input/parameters form. Read the
    // live ref, never a stale getSteps closure.
    const heroesIdx = stepsRef.current.indexOf('heroes');
    const editIdx = heroesIdx >= 0 ? heroesIdx : stepsRef.current.indexOf('storyInput');
    const target = editIdx >= 0 ? editIdx : stepsRef.current.indexOf('output');
    if (target >= 0) setCurrentStep(target + 1);
  }, []);

  // Simple dictation → STT → (LLM-refined brief when available). Prefill the
  // parameters form via the SAME mapping the agent path uses, so genre/age/length
  // land structured; fall back to the raw transcript as the prompt otherwise.
  const handleRecordComplete = useCallback((text: string, brief: StoryBrief | null, mode: 'compose' | 'verbatim') => {
    if (mode === 'verbatim') {
      // "Keep my dictation as is": from-text/verbatim — the transcript becomes
      // the story text, no LLM rewrite. Spoken by the parent = their own words,
      // so the paste-ToS affirmation is satisfied by construction.
      setParameters((p) => ({
        ...p,
        creationMode: 'from-text',
        fromTextMode: 'verbatim',
        text,
        title: p.title?.trim() || brief?.title?.trim() || p.title,
        fromTextTosAccepted: true,
      }));
      // The sequence re-resolves to the no-heroes variant on the next render;
      // land on the from-text form at its index in THAT sequence.
      const verbatimSeq = stepsRef.current.filter((s) => s !== 'heroes');
      setCurrentStep(verbatimSeq.indexOf('storyInput') + 1);
      return;
    }
    setParameters((p) =>
      brief
        ? { ...p, ...mapBriefToParameters(brief, p), creationMode: 'generate' }
        : { ...p, prompt: text, creationMode: 'generate' },
    );
    // Heroes first (record has no in-conversation picker — this is where the
    // parent links the dictated characters to the library), then the form.
    const heroesIdx = stepsRef.current.indexOf('heroes');
    const editIdx = heroesIdx >= 0 ? heroesIdx : stepsRef.current.indexOf('storyInput');
    if (editIdx >= 0) setCurrentStep(editIdx + 1);
  }, []);

  const handleStorySelect = useCallback((storyId: string, storyTitle: string, blockCount?: number, storyLanguage?: string, totalChars?: number) => {
    setSourceStoryId(storyId);
    setSourceStoryTitle(storyTitle);
    if (blockCount != null) setSourceStoryBlockCount(blockCount);
    if (totalChars != null) setSourceStoryTotalChars(totalChars);
    // Sync language so voice resolution uses the source story's locale, not
    // the page locale. Without this, picking "male" on a cs story while the UI
    // is in ru returns the ru/male voice mapping (e.g. Костя) instead of cs/male.
    if (isLocale(storyLanguage)) {
      setParameters((p) => ({ ...p, language: storyLanguage }));
    }
  }, []);

  // ── Navigation validation ──
  /**
   * WHY the next button is dark.
   *
   * A disabled button with no reason is a dead end: a parent who pasted a story
   * and typed a title had everything they could see, and the gate was a copyright
   * checkbox further down the page. The step says what is still missing.
   */
  const photoNameMissing = photoHeroNameMissing(parameters);
  const nextBlockedReason = useCallback((): string | null => {
    // A photo hero whose name is still empty: the character is built off-screen,
    // so the name is the one thing we cannot invent.
    if (currentStepKey === 'heroes') {
      return (parameters.pendingPhotoHeroes ?? []).some((h) => !h.name?.trim()) || photoNameMissing
        ? t('storyInput.blocked.heroName')
        : null;
    }
    if (currentStepKey === 'output') {
      return wantAudio && !voiceSettings.voiceId ? t('storyInput.blocked.voice') : null;
    }
    if (currentStepKey !== 'storyInput') return null;
    if (parameters.creationMode !== 'from-text') {
      // The idea field: ten characters is the whole bar, and a dark button with
      // no reason is how a parent decides the product is broken.
      return parameters.prompt.length < 10 ? t('storyInput.blocked.prompt') : null;
    }
    const mode = parameters.fromTextMode ?? 'verbatim';
    const usingBasis = mode === 'based_on' && !!parameters.basisStoryId;
    if ((parameters.title?.length ?? 0) < 1) return t('storyInput.blocked.title');
    if (!usingBasis && (parameters.text?.length ?? 0) < 100) return t('storyInput.blocked.text');
    if (!usingBasis && !parameters.fromTextTosAccepted) return t('storyInput.blocked.tos');
    // v2 replaces by entity: the parse (ReplacePicker) and at least one pick.
    if (mode === 'recast_hero' && (parameters.replaceMap?.length ?? 0) < 1) {
      return t('storyInput.blocked.recastHero');
    }
    if (mode === 'incorporate' && (parameters.fromTextInjectHeroes?.length ?? 0) < 1) {
      return t('storyInput.blocked.injectHero');
    }
    return null;
  }, [currentStepKey, parameters.creationMode, parameters.fromTextMode, parameters.basisStoryId, parameters.title?.length, parameters.text?.length, parameters.prompt.length, parameters.pendingPhotoHeroes, photoNameMissing, parameters.fromTextTosAccepted, parameters.replaceMap?.length, parameters.fromTextInjectHeroes?.length, wantAudio, voiceSettings.voiceId, t]);

  const canGoNext = useCallback(() => {
    switch (currentStepKey) {
      case 'heroes':
        // Heroes is skippable ("no heroes → auto") — EXCEPT a photo-hero draft
        // that still has no name: we started it, so wait for the name.
        return !(parameters.pendingPhotoHeroes ?? []).some((h) => !h.name?.trim()) && !photoNameMissing;
      case 'voice':
        // A voice is always selected (a preset by default), so this only blocks
        // while a clone is still being made.
        return !!voiceSettings.voiceId;
      case 'dictate':
        // The voice agent auto-advances to `output` once it hands off a brief;
        // manual Next stays disabled until then.
        return false;
      case 'storySelect':
        return sourceStoryId !== null;
      case 'storyInput': {
        if (parameters.creationMode === 'from-text') {
          // recast needs at least one picked replacement character; incorporate
          // needs at least one character to weave in; based_on's hero is
          // optional (a pure inspired-by is fine); verbatim/translate need none.
          const mode = parameters.fromTextMode ?? 'verbatim';
          const heroOk =
            mode === 'recast_hero'
              ? (parameters.replaceMap?.length ?? 0) >= 1
              : mode === 'incorporate'
                ? (parameters.fromTextInjectHeroes?.length ?? 0) >= 1
                : true;
          // based_on seeded from a public Owlcat basis: the source text is loaded
          // server-side (not pasted), and the "text is mine" ToS doesn't apply.
          const usingBasis = mode === 'based_on' && !!parameters.basisStoryId;
          return (
            (parameters.title?.length ?? 0) >= 1
            && (usingBasis || (parameters.text?.length ?? 0) >= 100)
            && (usingBasis || !!parameters.fromTextTosAccepted)
            && heroOk
          );
        }
        return parameters.prompt.length >= 10;
      }
      case 'output':
        // At minimum, story is always selected; for extend mode need audio or book
        if (wizardMode === 'extend' && !(wantAudio || wantIllustrations)) return false;
        // If audio is selected, voice must be chosen
        if (wantAudio && !voiceSettings.voiceId) return false;
        return true;
      case 'translate':
        return targetLanguage !== '' && targetLanguage !== parameters.language;
      case 'review':
        return true;
      default:
        return false;
    }
  }, [currentStepKey, wizardMode, inputMode, sourceStoryId, photoNameMissing, parameters.pendingPhotoHeroes, parameters.creationMode, parameters.prompt.length, parameters.title?.length, parameters.text?.length, parameters.language, parameters.fromTextMode, parameters.replaceMap?.length, parameters.fromTextInjectHeroes?.length, parameters.fromTextTosAccepted, parameters.basisStoryId, wantAudio, wantIllustrations, voiceSettings.voiceId, targetLanguage]);

  const handleNext = () => {
    if (currentStep < reviewStepNum) {
      const next = currentStep + 1;
      // Push a history entry so browser Back returns here; popstate is the setter
      // on the way back. Skip history games when embedded as a modal.
      if (!asModal) {
        // Save the values FIRST: the router may mount a fresh copy of this screen for the new
        // URL, and that copy restores from sessionStorage — the persist effect below runs only
        // after render, so a «Next» right after typing carried the OLD prompt (e2e: 1 in 4).
        if (hydrated && storageKey) {
          saveWizardState({
            wizardMode, inputMode, parameters, wantAudio, wantIllustrations,
            illustrationStyle, illustrationFormat, voiceSettings, audioOptions, illustrationOptions, exportOptions,
            sourceStoryId, sourceStoryTitle, currentStep: next, planId: null,
          }, storageKey);
        }
        window.history.pushState(null, '', stepUrl(stepsRef.current[next - 1]));
      }
      setCurrentStep(next);
    }
  };

  const handleBack = () => {
    // Came here from the IDEA field (a long paste answered "this is my text")?
    // Then Back belongs to that field, not to the step before it — otherwise the
    // parent is thrown out of a form they never navigated to.
    if (
      currentStepKey === 'storyInput'
      && parameters.creationMode === 'from-text'
      && parameters.fromTextFromIdea
    ) {
      setParameters((p) => ({
        ...p,
        creationMode: 'generate',
        fromTextFromIdea: undefined,
        basisStoryId: undefined,
      }));
      return;
    }
    if (currentStep > 1 && currentStep <= totalSteps) {
      // ONE mechanism per context — never both, or the two decrements race and Back
      // overshoots (e.g. review → skips output → storyInput). Non-modal: walk browser
      // history ONLY; popstate (onPop) sets currentStep from the URL, exactly like the
      // browser Back button. Modal (no history): state only.
      if (asModal) {
        setCurrentStep((s) => s - 1);
      } else {
        window.history.back();
      }
    }
  };

  // ── Hero names in the story's language ──
  // A Russian "Мира" in a Czech tale is unreadable for the parent reading it
  // aloud and splits the cast for the illustrator, so the spelling is asked for
  // once — right before creation, when the language is finally known.
  const [nameAsks, setNameAsks] = useState<HeroNameAsk[]>([]);
  const [namesSettled, setNamesSettled] = useState(false);
  const contentLanguage = wizardMode === 'translate' ? targetLanguage : parameters.language;

  const handleHeroNamesConfirmed = async (chosen: Record<string, string>) => {
    const asks = nameAsks;
    setNameAsks([]);
    setNamesSettled(true);
    const library = asks
      .filter((a) => a.characterId && chosen[a.characterId!]?.trim())
      .map((a) => ({ characterId: a.characterId!, name: chosen[a.characterId!].trim() }));
    if (library.length > 0) {
      const { confirmHeroNames } = await import('@/contexts/characters/actions/heroNamesForLanguage');
      await confirmHeroNames({ language: contentLanguage, entries: library }).catch(() => undefined);
    }
    // Name-only heroes (a "from a photo" draft) have no row yet: the spelling
    // the parent chose IS the name the worker builds the character with.
    const renames = asks.filter((a) => !a.characterId);
    if (renames.length > 0) {
      setParameters((prev) => ({
        ...prev,
        pendingPhotoHeroes: (prev.pendingPhotoHeroes ?? []).map((h) => {
          const next = chosen[h.name]?.trim();
          return next && next !== h.name ? { ...h, name: next } : h;
        }),
        ...(prev.fromTextHeroName && chosen[prev.fromTextHeroName]?.trim()
          ? { fromTextHeroName: chosen[prev.fromTextHeroName].trim() }
          : {}),
      }));
    }
    // The parameters update above is async; create on the next tick with it applied.
    setTimeout(() => { void handleCreate(true); }, 0);
  };

  const createPayload = (p: ContentParameters = parameters) => buildCreateParams({
    scenario: scenario!,
    // scenario is non-null here (guard in handleCreate) ⇒ a mode was chosen.
    wizardMode: wizardMode!,
    parameters: p,
    wantAudio,
    wantIllustrations,
    voiceSettings,
    illustrationStyle,
    illustrationFormat,
    audioOptions,
    illustrationOptions,
    exportOptions,
    targetLanguage,
    translateTextOnly,
    sourceStoryId,
  });

  // «Спросить родителя»: add the heroes (keep → incorporate them; retell → retell with them) …
  const handleAskAdd = () => {
    setParameters((prev) => ({
      ...prev,
      heroesAddConfirmed: true,
      ...(askHeroes === 'keep' && prev.creationMode === 'from-text' && (prev.fromTextMode ?? 'verbatim') === 'verbatim'
        ? { fromTextMode: 'incorporate' as const }
        : {}),
    }));
    setAskHeroes(null);
    setCreateAfterAsk(true);
  };
  // … or take them out of the order.
  const handleAskRemove = () => {
    setParameters((prev) => ({ ...prev, characterIds: [], characterAssignments: [], photoHeroes: [], pendingPhotoHeroes: [], replaceMap: [] }));
    setAskHeroes(null);
    setCreateAfterAsk(true);
  };
  // the answer is in `parameters` on the next render — create from there (a timeout would read the old values)
  const [createAfterAsk, setCreateAfterAsk] = useState(false);
  useEffect(() => {
    if (!createAfterAsk) return;
    setCreateAfterAsk(false);
    void handleCreate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createAfterAsk, parameters]);

  // ── Create ──
  const handleCreate = async (namesAlreadyAsked = false) => {
    if (!scenario || isCreating) return;
    setError(null);
    setRateLimited(false);
    setAllowanceShort(null);

    // heroes the operation would not use → ask first (once answered, heroesAddConfirmed / no heroes)
    const ask = heroesAskOf(createPayload());
    if (ask) { setAskHeroes(ask); return; }

    if (!namesAlreadyAsked && !namesSettled) {
      const photoNames = (parameters.pendingPhotoHeroes ?? []).map((h) => h.name).filter(Boolean);
      const heroName = parameters.fromTextHeroName?.trim();
      if (parameters.characterIds.length > 0 || photoNames.length > 0 || heroName) {
        try {
          const { heroNamesForLanguage } = await import('@/contexts/characters/actions/heroNamesForLanguage');
          const res = await heroNamesForLanguage({
            characterIds: parameters.characterIds,
            extraNames: [...photoNames, ...(heroName ? [heroName] : [])],
            language: contentLanguage,
          });
          if (res.success && res.asks && res.asks.length > 0) {
            setNameAsks(res.asks);
            return;
          }
        } catch {
          // Asking is a courtesy, not a gate: never block creation on it.
        }
      }
      setNamesSettled(true);
    }

    setIsCreating(true);

    if (parameters.characterIds.length > 0) {
      saveRecentCharacters(parameters.characterIds);
      setRecentCharacterIds(loadRecentCharacters());
    }

    // Notify parent immediately so it can close the dialog / show loading
    onMutationStart?.();

    startMutation.mutate(
      createPayload(),
      {
        onSuccess: (result) => {
          if (!result.success) {
            // Throttle OR out-of-allowance → friendly upgrade nudge, NOT a hard error.
            if (result.code === 'INSUFFICIENT_ALLOWANCE' && result.allowance) {
              setAllowanceShort(result.allowance);
            } else if (result.code === 'TEXT_RATE_LIMIT' || result.code === 'INSUFFICIENT_ALLOWANCE') {
              setRateLimited(true);
            } else {
              setError(result.error);
            }
            setIsCreating(false);
            return;
          }

          if (result.data.storyId) {
            // Fleet analytics (consent-gated, prod-only inside): ids/enums/counts only.
            track('story_created', {
              story_id: result.data.storyId,
              // The JOB, so the share of each page's readers that reaches a
              // finished story can be read per job (wave 1, measurement).
              mode: `${job ?? wizardMode ?? 'new'}:${inputMode ?? 'generate'}`,
              language: parameters.language,
              hero_count: parameters.characterIds.length,
            });
            clearWizardState();
            if (storageKey) clearWizardState(storageKey);
            // Keep isCreating=true — spinner stays until navigation completes
            if (asModal) {
              const type = needsVoice ? 'audio' : wantIllustrations ? 'book' : 'story';
              onComplete?.({
                type,
                entityId: result.data.storyId,
                planId: result.data.planId,
              });
            } else {
              const safeLocale = locale || 'ru';
              // Straight to the story hub — the PackageRunProgress panel
              // there shows text/audio/book live; no intermediate screen.
              router.push(
                `/${safeLocale}/stories/${result.data.storyId}` +
                (result.data.planId ? `?plan=${result.data.planId}` : ''),
              );
            }
          }
        },
        onError: () => {
          setError(t('errors.generic'));
          setIsCreating(false);
        },
      },
    );
  };

  return (
    <div
      data-testid="content-create-page"
      className={cn(
        asModal
          // In the dialog the wizard IS the dialog: a 42rem column inside a
          // 48rem box left a margin on both sides and squeezed the controls
          // (the voice picker worst of all) for no reason — the dialog already
          // decides how wide the thing is.
          ? "flex h-full min-h-0 w-full flex-col"
          : "mx-auto flex max-w-2xl flex-col h-[calc(100dvh-4rem)] sm:h-auto sm:min-h-0 sm:space-y-4 sm:block"
      )}
    >
      {/* Title — hidden in modal mode */}
      {!asModal && (
        <div className="space-y-1">
          {/* The job's name, not "create content": the page a parent came
              from promised one thing, and this is still that thing. */}
          <h1 className="text-2xl font-bold tracking-tight">{job ? t(`jobs.${job}.title`) : t('title')}</h1>
        </div>
      )}

      {/* Stepper */}
      <div className="shrink-0 pb-4">
        <WizardStepper
          steps={[...steps]}
          currentStep={currentStep}
        />
      </div>

      {/* Step content */}
      <Card className={cn(
        "flex-1 min-h-0 overflow-y-auto border-0 shadow-none",
        !asModal && "sm:border sm:shadow-sm sm:flex-none sm:overflow-visible"
      )}>
        <CardContent className="pt-4 px-0 sm:px-6">
          {currentStepKey === 'heroes' && (
            <HeroesStep
              values={parameters}
              onChange={setParameters}
              locale={locale}
              recentCharacterIds={recentCharacterIds}
              onCharacterNamesChange={setCharacterNames}
              autoOpenPhoto={!!road?.autoOpenPhoto}
              title={job ? t(`jobs.${job}.heroesTitle`) : undefined}
              subtitle={job ? t(`jobs.${job}.heroesSubtitle`) : undefined}
            />
          )}

          {currentStepKey === 'voice' && (
            <VoiceChoiceStep
              locale={locale}
              language={parameters.language}
              voiceSettings={voiceSettings}
              onVoiceSettingsChange={setVoiceSettings}
              hasOwnVoices={lib.voiceCount > 0}
              preferOwnVoice={!!road?.preferOwnVoice}
            />
          )}

          {currentStepKey === 'dictate' && (
            <DictateStep
              locale={locale}
              childName={parameters.audienceChildName}
              // Composing together is its own conversation: more time, and the
              // agent is told several children may talk at once.
              mode={job === 'together' ? 'together' : 'solo'}
              onBrief={handleDictateBrief}
            />
          )}

          {currentStepKey === 'record' && (
            <RecordStep locale={locale} onComplete={handleRecordComplete} />
          )}

          {currentStepKey === 'storySelect' && (
            <StorySelectStep
              selectedStoryId={sourceStoryId}
              onSelect={handleStorySelect}
            />
          )}

          {currentStepKey === 'storyInput' && (
            <StoryInputStep
              values={parameters}
              onChange={setParameters}
              locale={locale}
              recentCharacterIds={recentCharacterIds}
              onCharacterNamesChange={setCharacterNames}
              heroesInFlow={steps.includes('heroes')}
              basedOnOnly={job === 'basedon'}
              characterNames={characterNames}
              title={job ? t(`jobs.${job}.plotTitle`) : undefined}
              subtitle={job ? t(`jobs.${job}.plotSubtitle`) : undefined}
            />
          )}

          {currentStepKey === 'output' && (
            <OutputStep
              wantAudio={wantAudio}
              wantIllustrations={wantIllustrations}
              onWantAudioChange={chooseAudio}
              onWantBookChange={setWantBook}
              voiceSettings={voiceSettings}
              onVoiceSettingsChange={setVoiceSettings}
              illustrationStyle={illustrationStyle}
              onBookStyleChange={setBookStyle}
              illustrationFormat={illustrationFormat}
              onBookFormatChange={setBookFormat}
              audioOptions={audioOptions}
              onAudioOptionsChange={setAudioOptions}
              illustrationOptions={illustrationOptions}
              onBookOptionsChange={setBookOptions}
              exportOptions={exportOptions}
              onExportOptionsChange={setExportOptions}
              language={parameters.language}
              isExtendMode={wizardMode === 'extend'}
            />
          )}

          {currentStepKey === 'translate' && (
            <TranslateStep
              sourceLanguage={parameters.language}
              targetLanguage={targetLanguage}
              onTargetLanguageChange={setTargetLanguage}
              sourceStoryTitle={sourceStoryTitle}
              hasBook={sourceLayers.hasBook}
              hasNarration={sourceLayers.hasNarration}
              textOnly={translateTextOnly}
              onTextOnlyChange={setTranslateTextOnly}
            />
          )}

          {currentStepKey === 'review' && scenario && (
            <ReviewStep
              scenario={scenario}
              parameters={wizardMode === 'translate' ? { ...parameters, language: targetLanguage } : parameters}
              voiceSettings={needsVoice ? voiceSettings : undefined}
              sourceStoryTitle={wizardMode === 'extend' || wizardMode === 'translate' ? sourceStoryTitle : undefined}
              wantAudio={wantAudio}
              wantIllustrations={wantIllustrations}
              illustrationStyle={wantIllustrations ? illustrationStyle : undefined}
              illustrationFormat={wantIllustrations ? illustrationFormat : undefined}
              audioOptions={wantAudio ? audioOptions : undefined}
              illustrationOptions={wantIllustrations ? illustrationOptions : undefined}
              characterNames={characterNames}
              sourceStoryBlockCount={sourceStoryBlockCount}
              sourceStoryTotalChars={sourceStoryTotalChars}
              publicConsent={parameters.publicConsent}
              onPublicConsentChange={(v) => setParameters((prev) => ({ ...prev, publicConsent: v }))}
              translationHasBook={sourceLayers.hasBook}
              translationHasNarration={sourceLayers.hasNarration}
              translationTextOnly={translateTextOnly}
            />
          )}

          {/* "Change" — everything the road decided for the parent is still
               theirs to change, one disclosure away, on the screen where they
               see what they are about to get. The roads dropped the output
               STEP, not the choice. */}
          {currentStepKey === 'review' && scenario && !steps.includes('output') && (
            <div className="mt-4 border-t pt-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowOutputEdit((v) => !v)}
                data-testid="review-edit-outputs"
              >
                {showOutputEdit ? t('actions.hideChange') : t('actions.change')}
              </Button>
              {showOutputEdit && (
                <div className="pt-3">
                  <OutputStep
                    wantAudio={wantAudio}
                    wantIllustrations={wantIllustrations}
                    onWantAudioChange={chooseAudio}
                    onWantBookChange={setWantBook}
                    voiceSettings={voiceSettings}
                    onVoiceSettingsChange={setVoiceSettings}
                    illustrationStyle={illustrationStyle}
                    onBookStyleChange={setBookStyle}
                    illustrationFormat={illustrationFormat}
                    onBookFormatChange={setBookFormat}
                    audioOptions={audioOptions}
                    onAudioOptionsChange={setAudioOptions}
                    illustrationOptions={illustrationOptions}
                    onBookOptionsChange={setBookOptions}
                    exportOptions={exportOptions}
                    onExportOptionsChange={setExportOptions}
                    language={parameters.language}
                    isExtendMode={wizardMode === 'extend'}
                  />
                </div>
              )}
            </div>
          )}

        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="shrink-0 bg-destructive/10 border border-destructive/20 rounded-md p-3">
          <p
            data-testid="content-wizard-error"
            className="text-sm text-destructive text-center"
          >
            {error}
          </p>
        </div>
      )}

      {/* The allowance cannot pay for the whole book → say how much, and the way out */}
      {allowanceShort && (
        <div
          className="shrink-0 space-y-2 rounded-md border border-primary/30 bg-primary/10 p-3 text-center"
          data-testid="content-wizard-allowance-short"
        >
          <p className="text-sm font-semibold text-foreground">{tShort('title')}</p>
          <p className="text-sm text-muted-foreground">{tShort('body', allowanceShort)}</p>
          <p className="text-sm text-muted-foreground">{tShort('hint')}</p>
          <button
            type="button"
            onClick={() => openSubscription(uiLocale, webApp)}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            data-testid="content-wizard-allowance-short-cta"
          >
            {tShort('cta')}
            <ChevronRight className="size-4" />
          </button>
        </div>
      )}

      {/* Free text-gen rate-limit → non-blocking soft-push to subscribe */}
      {rateLimited && (
        <div
          className="shrink-0 space-y-2 rounded-md border border-primary/30 bg-primary/10 p-3 text-center"
          data-testid="content-wizard-rate-limit"
        >
          <p className="text-sm font-semibold text-foreground">{tRate('title')}</p>
          <p className="text-sm text-muted-foreground">{tRate('body')}</p>
          <button
            type="button"
            onClick={() => openSubscription(uiLocale, webApp)}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            data-testid="content-wizard-rate-limit-cta"
          >
            {tRate('cta')}
            <ChevronRight className="size-4" />
          </button>
        </div>
      )}

      {/* Navigation buttons */}
      {currentStep <= totalSteps && (
        <div className={cn(
          "flex flex-row gap-2 justify-between",
          asModal
            ? "shrink-0 pt-3 border-t"
            : "shrink-0 py-3 border-t sm:pt-4 sm:border-t-0"
        )}>
          {currentStep === 1 ? (
            <Button
              variant="outline"
              // Cancel goes back to the door: the tiles, where the jobs are.
              onClick={() => asModal
                ? onComplete?.({ type: '', entityId: '' })
                : router.push(`/${locale || 'ru'}/create`)}
              disabled={isCreating}
              data-testid="content-wizard-cancel-button"
            >
              <X className="mr-1 size-4" />
              {t('actions.cancel')}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={isCreating}
              data-testid="content-wizard-back-button"
            >
              <ChevronLeft className="mr-1 size-4" />
              {t('actions.back')}
            </Button>
          )}

          {currentStep < reviewStepNum ? (
            <div className="flex min-w-0 items-center gap-2">
              {!canGoNext() && nextBlockedReason() && (
                <span className="min-w-0 text-right text-xs text-muted-foreground" data-testid="next-blocked-reason">
                  {nextBlockedReason()}
                </span>
              )}
              <Button
                onClick={handleNext}
                disabled={!canGoNext()}
                data-testid="content-wizard-next-button"
              >
                {t('actions.next')}
                <ChevronRight className="ml-1 size-4" />
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => handleCreate()}
              disabled={isCreating || !canGoNext()}
              data-testid="content-wizard-create-button"
            >
              {isCreating
                ? <Loader2 className="mr-1.5 size-4 animate-spin" />
                : <Sparkles className="mr-1 size-4" />}
              {isCreating
                ? t('actions.creating')
                : t('actions.create')}
            </Button>
          )}
        </div>
      )}

      <AskHeroesDialog
        kind={askHeroes}
        onAdd={handleAskAdd}
        onRemove={handleAskRemove}
        onCancel={() => setAskHeroes(null)}
      />
      <HeroNamesDialog
        open={nameAsks.length > 0}
        asks={nameAsks}
        languageLabel={t(`languages.${contentLanguage}` as never) || contentLanguage}
        busy={isCreating}
        onCancel={() => setNameAsks([])}
        onConfirm={(chosen) => { void handleHeroNamesConfirmed(chosen); }}
      />
    </div>
  );
}
